/**
 * Variant picker "no preselect" behaviour.
 *
 * Shopify always renders a product page with the first available variant selected.
 * When the variant picker block has "Do not preselect variant options" turned on,
 * this script strips that initial selection back out and holds the buy buttons
 * back until the shopper has picked a value for every single option.
 *
 * The buttons keep their normal look and stay clickable: clicking one before the
 * options are complete does nothing except reveal a warning above the buttons,
 * naming the options that are still missing.
 *
 * Markup contract (see snippets/product-variant-picker.liquid):
 * - <variant-selects data-no-preselect="true" data-no-preselect-message="...">
 * - every option group carries data-option-position and data-option-name and,
 *   for dropdowns, data-no-preselect-placeholder
 */
(() => {
  // Quick view injects the product page's own markup - this script tag included -
  // into its modal, and HTMLUpdateUtility.setInnerHTML re-runs it every time. The
  // first run sets everything up; later ones only have to pick up the picker that
  // was just inserted.
  if (window.variantNoPreselectLoaded) {
    if (typeof window.variantNoPreselectInit === 'function') window.variantNoPreselectInit();
    return;
  }
  window.variantNoPreselectLoaded = true;

  const PICKER_SELECTOR = 'variant-selects[data-no-preselect="true"]';
  const PLACEHOLDER_SELECTOR = 'option[data-no-preselect-placeholder]';
  const BLOCKED_CLASS = 'variant-no-preselect-blocked';
  const MESSAGE_CLASS = 'variant-no-preselect-message';

  // Which option positions the shopper has actually picked, per section. The
  // picker markup is swapped out on every variant change, so this cannot live
  // in the DOM.
  const chosenBySection = new Map();

  // Sections whose warning is on screen. Kept here for the same reason: the buy
  // buttons are re-rendered on every variant change, taking the warning with them.
  const warnedSections = new Set();

  const chosenFor = (sectionId) => {
    if (!chosenBySection.has(sectionId)) chosenBySection.set(sectionId, new Set());
    return chosenBySection.get(sectionId);
  };

  const pickerOf = (productInfo) => productInfo.querySelector(PICKER_SELECTOR);
  const groupsOf = (picker) => Array.from(picker.querySelectorAll('[data-option-position]'));

  /** Remove the server-rendered selection from a single option group. */
  const clearGroup = (group) => {
    group.querySelectorAll('input[type="radio"]').forEach((input) => {
      input.checked = false;
      input.removeAttribute('checked');
    });

    const select = group.querySelector('select');
    if (select) {
      Array.from(select.options).forEach((option) => option.removeAttribute('selected'));

      let placeholder = select.querySelector(PLACEHOLDER_SELECTOR);
      if (!placeholder) {
        placeholder = document.createElement('option');
        placeholder.setAttribute('data-no-preselect-placeholder', '');
        placeholder.value = '';
        placeholder.disabled = true;
        placeholder.textContent = group.dataset.noPreselectPlaceholder || '';
        select.prepend(placeholder);
      }
      // VariantSelects.updateSelectionMetadata() looks the previously selected
      // option up by its `selected` attribute and throws when there is none, so
      // the placeholder has to carry it while nothing real is picked.
      placeholder.setAttribute('selected', 'selected');
      select.selectedIndex = 0;
    }

    group.querySelectorAll('[data-selected-value]').forEach((element) => {
      const swatch = element.querySelector('.swatch');
      if (swatch) {
        swatch.style.setProperty('--swatch--background', 'unset');
        swatch.classList.add('swatch--unavailable');
      } else {
        element.textContent = '';
      }
    });
  };

  /** The dropdown placeholder setting, with {option} filled in with every option name. */
  const stickyPlaceholderTextFor = (productInfo) => {
    const picker = pickerOf(productInfo);
    if (!picker) return '';

    const names = groupsOf(picker)
      .map((group) => group.dataset.optionName)
      .filter(Boolean);

    return (picker.dataset.noPreselectPlaceholder || '').replace(/\{option\}/g, listNames(names));
  };

  /**
   * The sticky bar carries its own flat variant dropdown. Give it a placeholder
   * while the shopper has not chosen, so it does not advertise a variant the
   * buy buttons refuse to add.
   */
  const syncStickySelect = (productInfo, blocked) => {
    const select = productInfo.querySelector('#sticky__variant');
    if (!select) return;

    let placeholder = select.querySelector(PLACEHOLDER_SELECTOR);

    if (blocked) {
      if (!placeholder) {
        placeholder = document.createElement('option');
        placeholder.setAttribute('data-no-preselect-placeholder', '');
        placeholder.value = '';
        placeholder.disabled = true;
        select.prepend(placeholder);
      }
      // One dropdown for the whole variant, so it is labelled with every option
      // name at once: "Choose Color, Size and Handle material".
      placeholder.textContent = stickyPlaceholderTextFor(productInfo);
      select.selectedIndex = 0;
    } else if (placeholder) {
      placeholder.remove();
    }
  };

  /** The names of the options still waiting to be picked, e.g. ['Size', 'Color']. */
  const missingNamesOf = (picker, chosen) =>
    groupsOf(picker)
      .filter((group) => !chosen.has(group.dataset.optionPosition))
      .map((group) => group.dataset.optionName)
      .filter(Boolean);

  /** 'Size' / 'Size and Color' / 'Size, Color and Material'. */
  const listNames = (names) =>
    names.length < 2 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names.slice(-1)}`;

  /** The warning as it reads right now, with {option} filled in. */
  const messageTextFor = (productInfo) => {
    const picker = pickerOf(productInfo);
    if (!picker) return '';

    const names = missingNamesOf(picker, chosenFor(productInfo.dataset.section));
    if (names.length === 0) return '';

    return (picker.dataset.noPreselectMessage || '').replace(/\{option\}/g, listNames(names));
  };

  /** The row the warning is shown above, for whichever button was clicked. */
  const anchorFor = (element) => {
    const form = element.closest('product-form') || element.closest('form');
    return (form && form.querySelector('.product-form__buttons')) || element.parentElement;
  };

  const showMessage = (productInfo, element) => {
    const text = messageTextFor(productInfo);
    if (!text) return;

    const anchor = anchorFor(element);
    const parent = anchor && anchor.parentElement;
    if (!parent) return;

    let message = Array.from(parent.children).find((child) =>
      child.classList.contains(MESSAGE_CLASS)
    );

    if (!message) {
      message = document.createElement('div');
      message.className = MESSAGE_CLASS;
      message.setAttribute('role', 'alert');
      parent.insertBefore(message, anchor);
    }

    message.textContent = text;
    message.hidden = false;
    warnedSections.add(productInfo.dataset.section);
  };

  /**
   * Once shown, the warning follows the shopper: it renames the options that are
   * still missing after every pick, and comes back after the buy buttons are
   * re-rendered.
   */
  const refreshMessage = (productInfo) => {
    if (!warnedSections.has(productInfo.dataset.section)) return;

    const existing = productInfo.querySelector(`.${MESSAGE_CLASS}:not([hidden])`);
    if (existing) {
      existing.textContent = messageTextFor(productInfo);
      return;
    }

    const button = productInfo.querySelector('product-form button[name="add"]');
    if (button) showMessage(productInfo, button);
  };

  const hideMessages = (productInfo) => {
    warnedSections.delete(productInfo.dataset.section);
    productInfo.querySelectorAll('.' + MESSAGE_CLASS).forEach((message) => {
      message.hidden = true;
    });
  };

  /** Hold back or release every add-to-cart / dynamic checkout button in the section. */
  const setBlocked = (productInfo, blocked) => {
    // A disabled input is left out of the submitted form, so nothing can be added
    // while the selection is unfinished. The value itself is left alone: the theme
    // owns it, and it has to be there the moment the input is enabled again.
    productInfo.querySelectorAll('product-form input[name="id"]').forEach((input) => {
      input.disabled = blocked;
    });

    // The buttons keep their own disabled / sold-out state, owned by
    // product-info.js and product-sticky-cart.js. All this adds is a flag for the
    // click handler below to watch for, plus - when the merchant asked for it -
    // a stand-in label.
    const picker = pickerOf(productInfo);
    const buttonText = picker && picker.dataset.noPreselectButtonText;

    productInfo.querySelectorAll('product-form button[name="add"]').forEach((button) => {
      const label = button.querySelector('span') || button;

      if (blocked) {
        // A half-finished selection resolves to no variant at all, so the theme
        // disables the button and calls it "Unavailable". Undo that: the button
        // has to stay clickable to be able to answer with the warning.
        if (!button.dataset.noPreselectLabel && label.textContent.trim()) {
          button.dataset.noPreselectLabel = label.textContent;
        }
        button.dataset.noPreselectBlocked = 'true';
        button.removeAttribute('disabled');
        label.textContent = buttonText || button.dataset.noPreselectLabel || label.textContent;
        return;
      }

      delete button.dataset.noPreselectBlocked;

      // The main submit button is owned by product-info.js / product-sticky-cart.js,
      // which have already applied the correct add-to-cart / sold-out state by now.
      // Anything else is ours to put back.
      if (button.dataset.noPreselectLabel) {
        if (!button.id.startsWith('ProductSubmitButton-')) {
          label.textContent = button.dataset.noPreselectLabel;
        }
        delete button.dataset.noPreselectLabel;
      }
    });

    productInfo
      .querySelectorAll('.shopify-payment-button')
      .forEach((element) => element.classList.toggle(BLOCKED_CLASS, blocked));

    if (!blocked) hideMessages(productInfo);

    syncStickySelect(productInfo, blocked);
  };

  const sync = (productInfo) => {
    const picker = pickerOf(productInfo);
    if (!picker) return;

    const chosen = chosenFor(productInfo.dataset.section);
    const groups = groupsOf(picker);

    groups.forEach((group) => {
      if (!chosen.has(group.dataset.optionPosition)) clearGroup(group);
    });

    const complete =
      groups.length > 0 && groups.every((group) => chosen.has(group.dataset.optionPosition));

    setBlocked(productInfo, !complete);
    if (!complete) refreshMessage(productInfo);
  };

  const init = (root = document) => {
    root.querySelectorAll('product-info').forEach((productInfo) => {
      const picker = pickerOf(productInfo);
      if (!picker) return;

      // Every quick view starts from scratch: the modal is emptied when it closes,
      // so whatever was picked last time it was open no longer counts.
      if (productInfo.closest('quick-add-modal')) {
        chosenBySection.delete(productInfo.dataset.section);
        warnedSections.delete(productInfo.dataset.section);
      } else if (new URLSearchParams(window.location.search).has('variant')) {
        // A ?variant= link is an explicit choice - honour it and stay out of the way.
        const chosen = chosenFor(productInfo.dataset.section);
        groupsOf(picker).forEach((group) => chosen.add(group.dataset.optionPosition));
      }

      sync(productInfo);
    });
  };

  const onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const productInfo = target.closest('product-info');
    const picker = productInfo && pickerOf(productInfo);
    if (!picker) return;

    const chosen = chosenFor(productInfo.dataset.section);

    // Picking a variant in the sticky bar dropdown resolves every option at once,
    // and has to release the buy buttons just like a pick in the main picker does.
    if (target.id === 'sticky__variant' && target.value) {
      groupsOf(picker).forEach((group) => chosen.add(group.dataset.optionPosition));
      setTimeout(() => sync(productInfo), 0);
      return;
    }

    if (!target.closest(PICKER_SELECTOR)) return;

    const group = target.closest('[data-option-position]');
    if (!group) return;

    if (target.tagName === 'SELECT' && !target.value) {
      chosen.delete(group.dataset.optionPosition);
    } else {
      chosen.add(group.dataset.optionPosition);
    }

    // A pick either completes the set - and clears the warning - or leaves fewer
    // options for it to name. Deferred so the theme's own change handlers, which
    // may re-render the section, go first.
    setTimeout(() => sync(productInfo), 0);
  };

  /**
   * Swallow clicks on a held-back add-to-cart / dynamic checkout button and show
   * the warning instead. Runs in the capture phase so product-form.js never gets
   * to submit. The dynamic checkout button is rendered by Shopify, so its inner
   * markup has pointer events turned off in CSS and the click lands on the
   * wrapper flagged here.
   */
  const onClick = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const blocked =
      target.closest('button[name="add"][data-no-preselect-blocked]') ||
      target.closest('.' + BLOCKED_CLASS);
    if (!blocked) return;

    const productInfo = blocked.closest('product-info');
    const picker = productInfo && pickerOf(productInfo);
    if (!picker) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    showMessage(productInfo, blocked);
  };

  /** The same guard for a keyboard submit, which never passes through the button. */
  const onSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const button = form.querySelector('button[name="add"][data-no-preselect-blocked]');
    if (!button) return;

    const productInfo = form.closest('product-info');
    const picker = productInfo && pickerOf(productInfo);
    if (!picker) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    showMessage(productInfo, button);
  };

  const start = () => {
    document.addEventListener('change', onChange, true);
    document.addEventListener('click', onClick, true);
    document.addEventListener('submit', onSubmit, true);

    // Subscribed here rather than at parse time so this runs *after* the
    // subscribers registered by product-info.js and product-sticky-cart.js,
    // whose updates we need to correct.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        // Quick view renames the section on the element it injects and reports the
        // original id in the event, so both attributes have to be matched.
        document
          .querySelectorAll(
            `product-info[data-section="${data.sectionId}"], product-info[data-original-section="${data.sectionId}"]`
          )
          .forEach((productInfo) => sync(productInfo));
      });
    }

    init();
    document.addEventListener('shopify:section:load', (event) => init(event.target));

    // Entry point for the re-runs described at the top of the file.
    window.variantNoPreselectInit = () => init();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
